import React, { useContext, useEffect } from "react"
import { StyleSheet, Text, View } from "react-native"
import { ShadowView, textStyles } from "./style"
import { MAX_CONTENT_WIDTH } from "./config"
import { ThemeContext } from "./utility/context/ThemeContext"
import Bullet from "./style/bullet"
import { useAppDispatch, useAppSelector } from './store'
import { clearCustomerImpact, fetchCustomerImpact } from "./customer-impact-slice"
import { SavingsImpactSkeleton } from "./savings-impact-skeleton"
import {
  EnvironmentalImpactSection,
  SocialImpactChart,
} from "./components/impact/index"
import type {
  CustomerSavingsImpactType,
  SocialImpactChartProps,
} from "./components/impact/index"

const CustomerSavingsImpact: React.FC = () => {
  const { user } = useAppSelector(s => s.auth)
  const { data: customerSavingsImpact, status, error } = useAppSelector(s => s.customerImpactSlice)

  const hasCustomer = !!user?.customer
  const loading = hasCustomer && (status === "loading" || status === "idle")

  const dispatch = useAppDispatch()

  const theme = useContext(ThemeContext)
  const { bgMain, danger, textMain } = theme.theme.colors

  useEffect(() => {
    if (user?.customer) {
      dispatch(fetchCustomerImpact())
    } else {
      dispatch(clearCustomerImpact())
    }
  }, [user?.customer, dispatch])

  return (
    <View style={{ alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH, paddingHorizontal: 16 }}>
      <Text style={[textStyles.h2, { marginBottom: 10, color: textMain }]}>
        Savings Impact
      </Text>
      <ShadowView
        style={[styles.card, { backgroundColor: bgMain }]}
      >
        {
          user.customer?.savingsImpact?.message && (
            <Text style={[textStyles.body, { marginBottom: 10, color: textMain }]}>
              {user.customer.savingsImpact.message}
            </Text>
          )
        }
        <View style={{}}>
          {
            !error ? <>
              {
                loading ? (
                  <SavingsImpactSkeleton />
                ) : (
                  hasCustomer &&customerSavingsImpact ? (
                    <View>
                      <View style={{ marginBottom: 16 }}>
                        <EnvironmentalImpactSection
                          customerSavingsImpact={customerSavingsImpact as CustomerSavingsImpactType}
                        />
                      </View>
                      <View style={{ flexDirection: "column", marginTop: 16 }}>
                        <Text
                          style={[
                            textStyles.h3,
                            { marginBottom: 10, color: textMain },
                          ]}
                        >
                          Social Impact
                        </Text>
                        <SocialImpactChart
                          surface="inApp"
                          chartData={
                            (Array.isArray(customerSavingsImpact?.["Social_Impact"])
                              ? customerSavingsImpact["Social_Impact"]
                              : []) as SocialImpactChartProps["chartData"]
                          }
                        />
                      </View>
                    </View>
                  ) : null
                )
              }
            </> : (
              <View style={{ gap: 10, alignItems: "flex-start", flexDirection: "row" }}>
                <Bullet color={danger} size={6} style={{ paddingRight: 6 }} />
                <Text style={[textStyles.body, { color: textMain }]}>
                  {error}
                </Text>
              </View>
            )
          }
        </View>
      </ShadowView>
    </View>
  )
}

export default CustomerSavingsImpact

const styles = StyleSheet.create({
  summaryCard: {
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 5,
    borderRadius: 10,
    padding: 15,
  },
  card: {
    borderRadius: 10,
    overflow: "hidden",
    flexDirection: "column",
    padding: 16,
  },
  dataPoints: {
    paddingHorizontal: 16,
    flexDirection: "row",
    marginBottom: 5,
    borderWidth: 1,
  },
  dataPointCol: {
    flexDirection: "column",
  },
  progressBar: {
    width: "100%",
    height: 50,
    overflow: "hidden",
    backgroundColor: "red",
    flexDirection: "row",
  },
  progressBarLabels: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 10,
    gap: 5,
  },
  progressBarLabel: {
    flexDirection: "row",
    alignItems: "center",
  },
})
